import { gql } from '@apollo/client';
import { ReportSectionState } from '@mnd-frontend/graphql-schema';
import { Icons, Text, media, useToasts } from '@mnd-frontend/ui';
import styled from 'styled-components';
import { useReportUpdateInsightMutation } from '../../../../generated/hooks';
import type { Report2SectionInsightsFragment } from '../../../../generated/types';
import { useReportEditable, useReportTracking, useReportTranslation } from '../../hooks';
import type { TranslationFunction } from '../../types';
import { TextInput } from '../common/TextInput';
import { AllowNewLine } from './AllowNewline';
import { Section } from './Section';
import { SkeletonSummaryInsight } from './Skeleton';

export const REPORT2_SECTION_INSIGHT_FRAGMENT = gql`
  fragment Report2SectionInsights on ReportSectionInsights {
    id
    title
    state
    insights2 {
      id
      insight
      insightType
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0rem;
  ${media.lessThan('tablet')} {
    flex-wrap: wrap;
  }
`;

const InsightWrapper = styled.div`
  width: 100%;
  padding: 1.5rem;
  h3 {
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
`;

const insightTitle = ({
  insightType,
  t,
}: {
  insightType: string;
  t: TranslationFunction;
}): string | null => {
  switch (insightType) {
    case 'SummaryVisibilityInsight':
      return t('reports_insights_visibility');
    case 'SummaryPerceptionInsight':
      return t('reports_insights_perception');
    case 'VisibilityTopicsInsight':
      return t('reports_insights_topics_and_themes');
    case 'VisibilityTypesOfMediaInsight':
      return t('reports_insights_types_of_media');
    case 'VisibilityBenchmarkSimilaritiesInsight':
      return t('reports_insights_similarities');
    case 'VisibilityBenchmarkDifferencesInsight':
      return t('reports_insights_benchmark_differences');
    case 'PerceptionPositiveInsight':
      return t('reports_insights_perception_positive');
    case 'PerceptionNegativeInsight':
      return t('reports_insights_perception_negative');
    case 'PerceptionNeutralInsight':
      return t('reports_insights_perception_neutral');
    case 'PerceptionBenchmarkPositiveInsight':
      return t('reports_insights_perception_benchmark_positive');
    case 'PerceptionBenchmarkNegativeInsight':
      return t('reports_insights_perception_benchmark_negative');
    case 'PerceptionBenchmarkNeutralInsight':
      return t('reports_insights_perception_benchmark_neutral');
    default:
      return null;
  }
};

const IconWrapper = styled.div`
  color: var(--coverage-brand-color);
  width: 4rem;
  height: 4rem;
  svg,
  i {
    width: 100%;
    height: 100%;
  }
`;

const Icon = ({ insightType }: { insightType: string }) => {
  switch (insightType) {
    case 'SummaryVisibilityInsight':
      return <Icons.Visibility />;
    case 'SummaryPerceptionInsight':
      return <Icons.SentimentPositive />;
    case 'VisibilityTopicsInsight':
      return <Icons.Lightbulb />;
    case 'VisibilityTypesOfMediaInsight':
      return <Icons.Story />;
    case 'VisibilityBenchmarkSimilaritiesInsight':
      return <Icons.Similarities />;
    case 'VisibilityBenchmarkDifferencesInsight':
      return <Icons.Differences />;
    case 'PerceptionPositiveInsight':
      return <Icons.SentimentPositive />;
    case 'PerceptionNegativeInsight':
      return <Icons.SentimentNegative />;
    case 'PerceptionNeutralInsight':
      return <Icons.SentimentNeutral />;
    case 'PerceptionBenchmarkPositiveInsight':
      return <Icons.SentimentPositive />;
    case 'PerceptionBenchmarkNegativeInsight':
      return <Icons.SentimentNegative />;
    case 'PerceptionBenchmarkNeutralInsight':
      return <Icons.SentimentNeutral />;

    default:
      return null;
  }
};

gql`
  mutation ReportUpdateInsight($reportInsightId: ID!, $insight: String, $title: String) {
    reportUpdateInsight(
      input: { reportInsightId: $reportInsightId, insight: $insight, title: $title }
    ) {
      insight {
        id
        title
        insight
        insightType
      }
      error {
        message
      }
    }
  }
`;

const SectionInsight = ({
  insight,
  editable,
}: {
  insight: Report2SectionInsightsFragment['insights2'][0];
  editable: boolean;
}) => {
  const { t } = useReportTranslation();
  const { showFailureToast } = useToasts();
  const { track } = useReportTracking();

  const [updateInsight] = useReportUpdateInsightMutation({
    onCompleted: data => {
      const error = data.reportUpdateInsight?.error;
      const errorMessage = error?.message;
      if (errorMessage) {
        showFailureToast(errorMessage);
      }
    },
    onError: () => showFailureToast(),
  });

  return (
    <InsightWrapper key={insight.id}>
      <IconWrapper>
        <Icon insightType={insight.insightType} />
      </IconWrapper>
      <Text
        as="h3"
        variant="reportHeadlineLarge"
        mobileVariant="reportHeadlineMedium"
        color="GREY_900"
      >
        {insightTitle({ insightType: insight.insightType, t })}
      </Text>
      <Text as="p" variant="body" mobileVariant="bodySmall" color="GREY_800">
        {editable ? (
          <TextInput
            allowNewline
            testId={`insight-${insight.insightType}-insight`}
            initialValue={insight.insight}
            onChange={value => {
              track({
                event: 'Brand Reports: Update AI insight',
                traits: { insight: insight.insightType },
              });
              updateInsight({
                variables: {
                  reportInsightId: insight.id,
                  insight: value,
                },
              });
            }}
            placeholder={t('common_insights')}
          />
        ) : (
          <AllowNewLine>{insight.insight}</AllowNewLine>
        )}
      </Text>
    </InsightWrapper>
  );
};

export const SectionInsights = ({ section }: { section: Report2SectionInsightsFragment }) => {
  const { editable } = useReportEditable();
  if (section.state !== ReportSectionState.Done) {
    return (
      <Section>
        <Wrapper>
          <InsightWrapper>
            <SkeletonSummaryInsight />
          </InsightWrapper>
          <InsightWrapper>
            <SkeletonSummaryInsight />
          </InsightWrapper>
        </Wrapper>
      </Section>
    );
  }
  if (section?.insights2?.length === 0) return null;

  return (
    <Section>
      <Wrapper>
        {(section.insights2 || []).map(insight => {
          return insight ? (
            <SectionInsight editable={editable} key={insight.id} insight={insight} />
          ) : null;
        })}
      </Wrapper>
    </Section>
  );
};
